import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  BadgeCheck,
  ClipboardCheck,
  FileText,
  Globe2,
  LayoutTemplate,
  Mail,
  Megaphone,
  PenLine,
  PlaySquare,
  Send,
  Sparkles
} from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";
type ContentType = "campaign" | "video" | "product" | "email";

const templates: Array<{ id: ContentType; icon: React.ElementType; zh: string; en: string; desc: string }> = [
  { id: "campaign", icon: Megaphone, zh: "营销文案", en: "Campaign", desc: "活动主题、卖点、转化话术" },
  { id: "video", icon: PlaySquare, zh: "短视频脚本", en: "Video Script", desc: "开场钩子、分镜、口播" },
  { id: "product", icon: FileText, zh: "商品描述", en: "Product Copy", desc: "详情页、卖点、FAQ" },
  { id: "email", icon: Mail, zh: "客户邮件", en: "Email", desc: "邀约、召回、跟进" }
];

const copy = {
  zh: {
    brand: "启序内容工坊",
    lang: "EN",
    nav: ["模板", "生成", "审核"],
    label: "独立案例站点 04 / AI 内容生成平台",
    title: "一个能把简报变成可审核内容的 AI 创作网站。",
    subtitle:
      "面向市场、销售和运营团队，把产品卖点、目标人群、渠道与语气转成营销文案、短视频脚本、商品描述和客户邮件。",
    primary: "开始生成",
    secondary: "查看模板",
    templateTitle: "内容模板库",
    templateSubtitle: "每个模板对应不同输出结构，方便业务直接复用。",
    studioTitle: "AI 内容生成工作台",
    studioSubtitle: "后端直连 MiniMax，根据简报、渠道和语气生成内容与审核风险点。",
    briefLabel: "需求简报",
    brief:
      "产品：企业知识库 RAG 平台。目标人群：中大型企业的 IT、客服和行政团队。核心卖点：回答带来源、低置信转人工、支持制度文档检索。目标：发布一篇公众号活动文案，引导预约演示。",
    tone: "语气",
    channel: "渠道",
    generate: "生成内容",
    sample: "填入示例",
    waiting: "等待输入简报。",
    reviewTitle: "审核与运营",
    reviewSubtitle: "AI 内容不能直接裸发，需要保留事实核查、渠道适配和复用资产。",
    panels: [
      ["事实核查", "客户案例、价格、效果承诺和合规表达进入人工审核。"],
      ["多渠道改写", "公众号长文、朋友圈短句、销售私聊话术可以分层生成。"],
      ["资产沉淀", "高转化标题、常用卖点和审核意见进入团队内容库。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 4 个真实独立网站"
  },
  en: {
    brand: "Qixu Content Studio",
    lang: "中文",
    nav: ["Templates", "Generate", "Review"],
    label: "Standalone Case Site 04 / AI Content Generation",
    title: "An AI studio that turns briefs into review-ready content.",
    subtitle:
      "For marketing, sales and ops teams: generate campaign copy, video scripts, product descriptions and customer emails from a structured brief.",
    primary: "Start generating",
    secondary: "View templates",
    templateTitle: "Template Library",
    templateSubtitle: "Each template uses a different output structure for direct business reuse.",
    studioTitle: "AI Content Desk",
    studioSubtitle: "The backend calls MiniMax directly and generates content with review risks.",
    briefLabel: "Brief",
    brief:
      "Product: enterprise RAG knowledge platform. Audience: IT, support and admin teams in mid-to-large companies. Key values: cited answers, low-confidence escalation and policy document search. Goal: publish a campaign post to drive demo bookings.",
    tone: "Tone",
    channel: "Channel",
    generate: "Generate",
    sample: "Use sample",
    waiting: "Waiting for a brief.",
    reviewTitle: "Review and Operations",
    reviewSubtitle: "AI content needs fact-checking, channel adaptation and reusable assets before publishing.",
    panels: [
      ["Fact check", "Customer cases, pricing, performance claims and compliance wording require review."],
      ["Channel rewrite", "Generate long posts, social snippets and sales chat scripts in layers."],
      ["Asset memory", "Save high-converting titles, value props and review feedback."]
    ],
    footer: "Qixu Technology AI case site · fourth real standalone website"
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [type, setType] = useState<ContentType>("campaign");
  const [brief, setBrief] = useState(t.brief);
  const [tone, setTone] = useState("专业可信");
  const [channel, setChannel] = useState("公众号");
  const [result, setResult] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setBrief(copy[next].brief);
    setTone(next === "zh" ? "专业可信" : "professional and credible");
    setChannel(next === "zh" ? "公众号" : "LinkedIn");
    setResult(copy[next].waiting);
    setMeta("");
  }

  async function generate() {
    if (!brief.trim() || loading) return;
    setLoading(true);
    setResult(locale === "zh" ? "正在请求 MiniMax 生成内容..." : "Requesting MiniMax content...");
    setMeta("");
    try {
      const response = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief, tone, channel, type, locale })
      });
      const data = await response.json();
      setResult(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setResult(locale === "zh" ? "网络异常，无法生成内容。" : "Network error. Content unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top"><span>写</span><strong>{t.brand}</strong></a>
        <nav>
          <a href="#templates">{t.nav[0]}</a>
          <a href="#studio">{t.nav[1]}</a>
          <a href="#review">{t.nav[2]}</a>
        </nav>
        <button className="lang" onClick={switchLocale}><Globe2 size={16} />{t.lang}</button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#studio">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#templates">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><PenLine size={22} />Content pipeline</div>
          <div className="pipeline">
            <div><LayoutTemplate size={18} /> Brief</div>
            <div><Sparkles size={18} /> Generate</div>
            <div><ClipboardCheck size={18} /> Review</div>
            <div><Send size={18} /> Publish</div>
          </div>
        </div>
      </section>

      <section className="templates" id="templates">
        <div className="section-copy">
          <h2>{t.templateTitle}</h2>
          <p>{t.templateSubtitle}</p>
        </div>
        <div className="template-grid">
          {templates.map((item) => {
            const Icon = item.icon;
            return (
              <button className={type === item.id ? "active" : ""} key={item.id} onClick={() => setType(item.id)}>
                <Icon size={24} />
                <strong>{locale === "zh" ? item.zh : item.en}</strong>
                <span>{item.desc}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="studio" id="studio">
        <div className="desk">
          <div className="section-title">
            <PenLine size={22} />
            <div><h2>{t.studioTitle}</h2><p>{t.studioSubtitle}</p></div>
          </div>
          <label>{t.briefLabel}<textarea value={brief} onChange={(event) => setBrief(event.target.value)} /></label>
          <div className="controls">
            <label>{t.tone}<input value={tone} onChange={(event) => setTone(event.target.value)} /></label>
            <label>{t.channel}<input value={channel} onChange={(event) => setChannel(event.target.value)} /></label>
          </div>
          <div className="composer-actions">
            <button onClick={() => setBrief(t.brief)}>{t.sample}</button>
            <button className="primary" onClick={generate} disabled={loading || !brief.trim()}>{t.generate}<Sparkles size={16} /></button>
          </div>
        </div>
        <div className="result-card" aria-live="polite">
          <div className="result-head"><BadgeCheck size={20} /><strong>Generated output</strong></div>
          <p>{result}</p>
          {meta && <span>{meta}</span>}
        </div>
      </section>

      <section className="review" id="review">
        <div>
          <h2>{t.reviewTitle}</h2>
          <p>{t.reviewSubtitle}</p>
        </div>
        <div className="stats">
          <div><span>生成资产</span><strong>428</strong><em>本月</em></div>
          <div><span>审核通过</span><strong>86%</strong><em>稳定</em></div>
          <div><span>复用标题</span><strong>73</strong><em>已沉淀</em></div>
        </div>
        <div className="review-grid">
          {t.panels.map(([title, content]) => (
            <article key={title}><ClipboardCheck size={20} /><h3>{title}</h3><p>{content}</p></article>
          ))}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
